import {useTheme} from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, {useCallback, useEffect, useRef, useState} from 'react'

import useIsMobile from 'hooks/useIsMobile'

import foundationLayer from './assets/hom-foundation.svg'
import humanLayer from './assets/hom-human.svg'
import performanceLayer from './assets/hom-performance.svg'

const SECTION_TITLE = 'How the Human Orchestrated Model™ works:'
const SECTION_SUBTITLE =
  'Data enables intelligence. Governance directs it. Measurement improves it.'

const CALLOUTS = [
  {
    id: 'performance',
    title: 'Performance Steering',
    body: [
      'We address the value and use case selection challenges by embedding measurement directly into the design of intelligent systems.',
      'Ensuring that every intelligent capability is tied to real business impact & continuously learns to improve performance.',
    ],
  },
  {
    id: 'human',
    title: 'Human in Command',
    body: [
      'We address your business function alignment challenges by transforming AI from a technical capability into a governed organizational system.',
      'Ensuring intelligence operates within a controlled, transparent, and responsible framework, avoiding agentic chaos.',
    ],
  },
  {
    id: 'foundation',
    title: 'The intelligence foundation',
    body: [
      'We address your data & financial rigour challenges by building strong, governed, and operational data foundations.',
      'Enabling intelligent systems to understand context and act effectively. Moving from experimental AI, to industrialized AI.',
    ],
  },
]

// Each tier is an independent vector exported from Figma (node 2060:5759 /
// 2060:5760 / 2060:5761). The percentages below stack them into the original
// tower; they tile exactly so the connecting cylinders line up.
const LAYER_ORDER = ['performance', 'human', 'foundation']

const LAYER_CONFIG = {
  performance: {
    src: performanceLayer,
    top: '0%',
    left: '17.38%',
    width: '63.73%',
    glow: 'rgba(6, 189, 255, 0.5)',
  },
  human: {
    src: humanLayer,
    top: '37.7%',
    left: '17.32%',
    width: '67.05%',
    glow: 'rgba(243, 150, 32, 0.5)',
  },
  foundation: {
    src: foundationLayer,
    top: '68.99%',
    left: '0%',
    width: '100%',
    glow: 'rgba(223, 10, 33, 0.48)',
  },
}

const STACK_ASPECT = '692 / 1919'

// Fixed vertical anchors (as % of the tower height) for the mobile accordion,
// roughly aligned to each layer. Headers stay put on expand; an open card
// overlays downward over the callout(s) below it (mirrors the Figma maquette).
const MOBILE_CALLOUT_TOP = {
  performance: '13%',
  human: '45%',
  foundation: '76%',
}

const LAYER_TRANSITION =
  'opacity 0.55s cubic-bezier(0.25, 1, 0.5, 1), filter 0.55s cubic-bezier(0.25, 1, 0.5, 1), transform 0.45s cubic-bezier(0.25, 1, 0.5, 1)'

const ToggleIcon = ({expanded}) => (
  <svg
    aria-hidden="true"
    fill="none"
    height="12"
    viewBox="0 0 12 12"
    width="12"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1 6h10"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.5"
    />
    {!expanded && (
      <path
        d="M6 1v10"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.5"
      />
    )}
  </svg>
)

ToggleIcon.propTypes = {expanded: PropTypes.bool}
ToggleIcon.defaultProps = {expanded: false}

const getStyles = (theme, isMobile) => {
  const palette = theme.palette || {}
  const background = palette.background || {}
  const text = palette.text || {}
  const deepNavy = background.deepNavy || '#171531'
  const white = text.light || '#ffffff'

  return {
    root: {
      position: 'relative',
      width: '100%',
      overflow: 'hidden',
      background: `
        radial-gradient(40% 30% at 20% 100%, rgba(243, 150, 32, 0.26) 0%, rgba(223, 10, 33, 0.14) 38%, rgba(23, 21, 49, 0) 76%),
        radial-gradient(38% 34% at 80% 100%, rgba(6, 189, 255, 0.25) 0%, rgba(243, 150, 32, 0.16) 45%, rgba(23, 21, 49, 0) 78%),
        linear-gradient(180deg, ${deepNavy} 0%, #100f25 100%)
      `,
      color: white,
      padding: isMobile ? '72px 16px 96px' : '116px 24px 136px',
      scrollMarginTop: isMobile ? 20 : 90,
    },
    inner: {
      position: 'relative',
      maxWidth: 1280,
      margin: '0 auto',
      zIndex: 1,
    },
    header: {
      maxWidth: 960,
      margin: isMobile ? '0 auto 42px' : '0 auto 72px',
      textAlign: 'center',
    },
    title: {
      margin: 0,
      color: white,
      fontSize: isMobile ? 22 : 'clamp(30px, 4.4vw, 58px)',
      fontWeight: 800,
      lineHeight: isMobile ? 1.22 : 1.12,
    },
    subtitle: {
      margin: '14px 0 0',
      color: 'rgba(255, 255, 255, 0.86)',
      fontSize: isMobile ? 16 : 'clamp(17px, 2vw, 27px)',
      fontWeight: 400,
      lineHeight: isMobile ? 1.3 : 1.35,
    },
    body: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    stack: {
      position: 'relative',
      width: isMobile ? 'min(320px, 80vw)' : 'min(420px, 34vw)',
      aspectRatio: STACK_ASPECT,
      margin: isMobile ? '0 0 44px' : 0,
    },
    layer: {
      position: 'absolute',
      height: 'auto',
      transition: LAYER_TRANSITION,
      transformOrigin: 'center center',
      willChange: 'opacity, filter, transform',
    },
    stageMobile: {
      position: 'relative',
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-start',
    },
    stackMobile: {
      position: 'relative',
      flex: '0 0 auto',
      width: 'min(215px, 54vw)',
      marginLeft: '1%',
      aspectRatio: STACK_ASPECT,
    },
    accordionMobile: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      width: '58%',
      zIndex: 2,
    },
    calloutBlockMobile: {
      position: 'absolute',
      left: 0,
      right: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    calloutHeaderMobile: {
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
    },
    toggleButton: {
      flex: '0 0 auto',
      width: 36,
      height: 36,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 999,
      border: '1px solid rgba(255, 255, 255, 0.22)',
      background: 'rgba(190, 195, 215, 0.22)',
      color: '#ffffff',
      cursor: 'pointer',
      padding: 0,
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
      transition: 'background 0.35s ease, border-color 0.35s ease',
    },
    bodyCardMobile: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      marginTop: 14,
      padding: '16px 18px',
      borderRadius: 18,
      background: 'rgba(8, 10, 26, 0.82)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      boxShadow: '0 18px 44px rgba(0, 0, 0, 0.32)',
    },
    callout: {
      color: 'rgba(255, 255, 255, 0.58)',
      transition: 'color 0.45s ease, transform 0.45s ease',
      cursor: isMobile ? 'default' : 'pointer',
    },
    calloutDesktop: {
      position: 'absolute',
      width: 'clamp(220px, 22vw, 330px)',
    },
    performanceCallout: {
      top: '18%',
      left: 'calc(100% + 28px)',
      textAlign: 'left',
    },
    humanCallout: {
      top: '40%',
      right: 'calc(100% + 28px)',
      textAlign: 'right',
    },
    foundationCallout: {
      top: '66%',
      left: 'calc(100% + 28px)',
      textAlign: 'left',
    },
    calloutTitle: {
      margin: isMobile ? 0 : '0 0 18px',
      paddingTop: isMobile ? 5 : 0,
      fontSize: isMobile ? 14 : 19,
      fontWeight: 800,
      lineHeight: 1.25,
      transition: 'color 0.45s ease',
    },
    calloutBody: {
      margin: '0 0 16px',
      fontSize: isMobile ? 12 : 15,
      lineHeight: 1.45,
      transition: 'color 0.45s ease',
    },
  }
}

const KeyrusAiHom = ({copy}) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const styles = getStyles(theme, isMobile)

  const title = copy.title || SECTION_TITLE
  const subtitle = copy.subtitle || SECTION_SUBTITLE
  const calloutCopy = copy.callouts || {}
  const callouts = CALLOUTS.map(callout => ({
    ...callout,
    title: calloutCopy[callout.id]?.title || callout.title,
    body: calloutCopy[callout.id]?.body || callout.body,
  }))

  const calloutRefs = useRef({})
  const stageRef = useRef(null)
  const mobileBlockRefs = useRef({})
  const [activeLayer, setActiveLayer] = useState(null)
  const [hoveredLayer, setHoveredLayer] = useState(null)
  const [spacerHeight, setSpacerHeight] = useState(0)

  const registerCallout = useCallback(
    id => node => {
      calloutRefs.current[id] = node
    },
    [],
  )

  useEffect(() => {
    if (isMobile || typeof window === 'undefined') {
      return undefined
    }

    if (!('IntersectionObserver' in window)) {
      setActiveLayer('performance')

      return undefined
    }

    const nodes = LAYER_ORDER.map(id => calloutRefs.current[id]).filter(Boolean)

    if (!nodes.length) {
      return undefined
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.4) {
            setActiveLayer(entry.target.dataset.layer)
          }
        })
      },
      {
        rootMargin: '-35% 0px -35% 0px',
        threshold: [0.2, 0.5, 0.8],
      },
    )

    nodes.forEach(node => observer.observe(node))

    return () => observer.disconnect()
  }, [isMobile])

  useEffect(() => {
    if (isMobile) {
      setActiveLayer(prev => (prev === null ? 'performance' : prev))
    }
  }, [isMobile])

  const handleMobileToggle = useCallback(id => {
    setActiveLayer(prev => (prev === id ? null : id))
  }, [])

  // The mobile callouts sit at fixed anchors so the +/- toggles never move on
  // expand. An open card can therefore overflow the tower's bottom (the lowest
  // callout in particular). Measure that overflow and reserve matching space
  // below the stage so the card is never clipped by the section's overflow.
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined') {
      setSpacerHeight(0)

      return undefined
    }

    const measure = () => {
      const stage = stageRef.current
      const block = activeLayer ? mobileBlockRefs.current[activeLayer] : null

      if (!stage || !block) {
        setSpacerHeight(0)

        return
      }

      const overflow =
        block.getBoundingClientRect().bottom -
        stage.getBoundingClientRect().bottom

      setSpacerHeight(overflow > 0 ? Math.ceil(overflow) + 24 : 0)
    }

    measure()
    window.addEventListener('resize', measure)

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure)
    }

    return () => window.removeEventListener('resize', measure)
  }, [isMobile, activeLayer])

  const getLayerStyle = id => {
    const config = LAYER_CONFIG[id]
    const base = {
      ...styles.layer,
      top: config.top,
      left: config.left,
      width: config.width,
    }

    if (isMobile) {
      if (activeLayer === id) {
        return {
          ...base,
          opacity: 1,
          filter: `saturate(120%) brightness(108%) drop-shadow(0 0 16px ${config.glow})`,
          zIndex: 2,
        }
      }

      return {
        ...base,
        opacity: 0.32,
        filter: 'saturate(16%) brightness(52%)',
        zIndex: 1,
      }
    }

    const isHovered = hoveredLayer === id
    const isActive = activeLayer === id || isHovered
    const isDimmedByPeer = hoveredLayer && hoveredLayer !== id

    let opacity = 0.32
    let filter = 'saturate(18%) brightness(62%)'
    let transform = 'scale(1) translateY(0)'
    let zIndex = 1

    if (isActive) {
      opacity = 1
      filter = `saturate(120%) brightness(108%) drop-shadow(0 0 16px ${config.glow})`
      zIndex = 2
    }

    if (isDimmedByPeer) {
      opacity = 0.15
    }

    if (isHovered) {
      opacity = 1
      filter = `saturate(135%) brightness(112%) drop-shadow(0 14px 30px ${config.glow})`
      transform = 'scale(1.04) translateY(-8px)'
      zIndex = 3
    }

    return {...base, opacity, filter, transform, zIndex}
  }

  const calloutPositionStyles = {
    foundation: styles.foundationCallout,
    human: styles.humanCallout,
    performance: styles.performanceCallout,
  }

  const renderCallout = callout => {
    const isActive = activeLayer === callout.id || hoveredLayer === callout.id
    const titleColor = isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.62)'
    const bodyColor = isActive
      ? 'rgba(255, 255, 255, 0.82)'
      : 'rgba(255, 255, 255, 0.52)'

    const positionStyle = isMobile ? null : styles.calloutDesktop
    const placementStyle = isMobile ? null : calloutPositionStyles[callout.id]

    return (
      <article
        data-layer={callout.id}
        key={callout.id}
        onMouseEnter={isMobile ? undefined : () => setHoveredLayer(callout.id)}
        onMouseLeave={isMobile ? undefined : () => setHoveredLayer(null)}
        ref={registerCallout(callout.id)}
        style={{
          ...styles.callout,
          ...positionStyle,
          ...placementStyle,
        }}
      >
        <h3 style={{...styles.calloutTitle, color: titleColor}}>
          {callout.title}
        </h3>
        {callout.body.map(paragraph => (
          <p key={paragraph} style={{...styles.calloutBody, color: bodyColor}}>
            {paragraph}
          </p>
        ))}
      </article>
    )
  }

  const renderLayer = id => (
    <img
      alt={id === 'performance' ? 'Human Orchestrated Model framework' : ''}
      key={id}
      onMouseEnter={isMobile ? undefined : () => setHoveredLayer(id)}
      onMouseLeave={isMobile ? undefined : () => setHoveredLayer(null)}
      src={LAYER_CONFIG[id].src}
      style={getLayerStyle(id)}
    />
  )

  const renderMobileCallout = callout => {
    const isActive = activeLayer === callout.id

    return (
      <div
        key={callout.id}
        ref={node => {
          mobileBlockRefs.current[callout.id] = node
        }}
        style={{
          ...styles.calloutBlockMobile,
          top: MOBILE_CALLOUT_TOP[callout.id],
          zIndex: isActive ? 2 : 1,
        }}
      >
        <div style={styles.calloutHeaderMobile}>
          <button
            aria-expanded={isActive}
            aria-label={`${isActive ? 'Collapse' : 'Expand'} ${callout.title}`}
            onClick={() => handleMobileToggle(callout.id)}
            style={{
              ...styles.toggleButton,
              background: isActive
                ? 'rgba(28, 32, 56, 0.62)'
                : 'rgba(190, 195, 215, 0.22)',
              borderColor: isActive
                ? 'rgba(255, 255, 255, 0.3)'
                : 'rgba(255, 255, 255, 0.22)',
            }}
            type="button"
          >
            <ToggleIcon expanded={isActive} />
          </button>
          <h3
            style={{
              ...styles.calloutTitle,
              color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.6)',
            }}
          >
            {callout.title}
          </h3>
        </div>
        {isActive && (
          <div style={styles.bodyCardMobile}>
            {callout.body.map(paragraph => (
              <p
                key={paragraph}
                style={{
                  ...styles.calloutBody,
                  margin: 0,
                  color: 'rgba(255, 255, 255, 0.82)',
                }}
              >
                {paragraph}
              </p>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <section aria-labelledby="keyrus-ai-hom-title" id="hom" style={styles.root}>
      <div style={styles.inner}>
        <header style={styles.header}>
          <h2 id="keyrus-ai-hom-title" style={styles.title}>
            {title}
          </h2>
          <p style={styles.subtitle}>{subtitle}</p>
        </header>
        <div style={styles.body}>
          {isMobile ? (
            <>
              <div ref={stageRef} style={styles.stageMobile}>
                <div style={styles.stackMobile}>
                  {LAYER_ORDER.map(renderLayer)}
                </div>
                <div style={styles.accordionMobile}>
                  {callouts.map(renderMobileCallout)}
                </div>
              </div>
              {spacerHeight > 0 && (
                <div
                  aria-hidden="true"
                  style={{height: spacerHeight, width: '100%'}}
                />
              )}
            </>
          ) : (
            <div style={styles.stack}>
              {LAYER_ORDER.map(renderLayer)}
              {callouts.map(renderCallout)}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

KeyrusAiHom.propTypes = {
  copy: PropTypes.shape({
    callouts: PropTypes.objectOf(
      PropTypes.shape({
        body: PropTypes.arrayOf(PropTypes.string),
        title: PropTypes.string,
      }),
    ),
    subtitle: PropTypes.string,
    title: PropTypes.string,
  }),
}

KeyrusAiHom.defaultProps = {
  copy: {},
}

export default KeyrusAiHom
